import React, { useEffect, useState } from "react";
import {
    Container,
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    IconButton,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Chip,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { Add, Edit, Delete } from "@mui/icons-material";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { DateTimePicker } from "@mui/x-date-pickers";
import useAuthHeader from "react-auth-kit/hooks/useAuthHeader";
import { REACT_APP_BACKEND_URL } from "../../Utils/Language/urlBase";
import { useLang } from "../../context/LanguageContext";
import { formatDate } from "../../Utils/dateFormatter";

const Expenses = () => {
    const [expenses, setExpenses] = useState([]);
    const [filteredExpenses, setFilteredExpenses] = useState([]);
    const [open, setOpen] = useState(false);
    const [isRefresh, setIsRefresh] = useState(false);
    const [currentExpense, setCurrentExpense] = useState({
        id: null,
        name: "",
        amount: "",
        addedTime: new Date(),
    });
    const authHeader = useAuthHeader();
    const { t } = useLang();
    const [filter, setFilter] = useState({
        startDate: new Date(),
        endDate: new Date(),
    });

    const handleOpen = (
        expense = { id: null, name: "", amount: "", addedTime: new Date() },
    ) => {
        setCurrentExpense(expense);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setIsRefresh(!isRefresh);
        setCurrentExpense({
            id: null,
            name: "",
            amount: "",
            addedTime: new Date(),
        });
    };

    const handleSave = async () => {
        if (currentExpense.id) {
            await fetch(`/expenses/editExpense/${currentExpense.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    authorization: authHeader,
                },
            });
        }

        if (!currentExpense.id) {
            const response = await fetch(
                `${REACT_APP_BACKEND_URL}/expenses/addExpense`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        authorization: authHeader,
                    },
                    body: JSON.stringify(currentExpense),
                },
            );
            console.log(response);
        }
        handleClose();
    };

    const handleDelete = async (id) => {
        await fetch(`/api/expenses/deleteExpense/${id}`, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
        });
        handleClose();
    };

    const handleFilterChange = (field, value) => {
        setFilter({ ...filter, [field]: value });
    };

    useEffect(() => {
        const fetchExpenses = async () => {
            const response = await fetch(
                `${REACT_APP_BACKEND_URL}/expenses/getAllExpenses`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        authorization: authHeader,
                    },
                    body: JSON.stringify({
                        dateRange: filter,
                    }),
                },
            );
            if (response.ok) {
                const data = await response.json();
                console.log(data);
                setExpenses(data.expense);
            }
        };
        fetchExpenses();
    }, [isRefresh, authHeader, filter]);

    const totalAmount = (
        filteredExpenses.length ? filteredExpenses : expenses
    ).reduce((sum, e) => sum + Number(e.amount || 0), 0);

    return (
        <Container>
            {/* Modern Page Header */}
            <Paper
                elevation={0}
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                    p: 2.5,
                    borderRadius: 3,
                    background:
                        "linear-gradient(135deg, #f57c00 0%, #e65100 100%)",
                    color: "white",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <MoneyOffIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            {t("EXPENSES")}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                            <Chip
                                label={`${expenses.length} ${t("EXPENSES")}`}
                                size="small"
                                sx={{
                                    bgcolor: "rgba(255,255,255,0.2)",
                                    color: "white",
                                    fontWeight: 600,
                                }}
                            />
                            <Chip
                                label={`${t("TOTAL_PRICE")}: ${totalAmount.toFixed(2)}`}
                                size="small"
                                sx={{
                                    bgcolor: "rgba(255,255,255,0.15)",
                                    color: "white",
                                    fontWeight: 600,
                                }}
                            />
                        </Box>
                    </Box>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpen()}
                    sx={{
                        bgcolor: "rgba(255,255,255,0.2)",
                        color: "white",
                        border: "1.5px solid rgba(255,255,255,0.4)",
                        fontWeight: 700,
                        "&:hover": { bgcolor: "rgba(255,255,255,0.35)" },
                    }}
                >
                    {t("ADD_EXPENSE")}
                </Button>
            </Paper>

            <Paper
                sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: 3,
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                }}
            >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label={t("DATE")}
                        value={dayjs(filter.startDate)}
                        onChange={(date) =>
                            handleFilterChange("startDate", date)
                        }
                        slotProps={{ textField: { size: "small" } }}
                    />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                        label={t("DATE")}
                        value={dayjs(filter.endDate)}
                        onChange={(date) => handleFilterChange("endDate", date)}
                        slotProps={{ textField: { size: "small" } }}
                    />
                </LocalizationProvider>
            </Paper>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {(filteredExpenses.length
                        ? filteredExpenses
                        : expenses
                    ).map((exp) => (
                        <TableRow key={exp.id}>
                            <TableCell>{exp.name}</TableCell>
                            <TableCell>{exp.amount}</TableCell>
                            <TableCell>{formatDate(exp.addedTime)}</TableCell>
                            <TableCell>
                                <IconButton onClick={() => handleOpen(exp)}>
                                    <Edit />
                                </IconButton>
                                <IconButton
                                    onClick={() => handleDelete(exp.id)}
                                >
                                    <Delete />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>
                    {currentExpense.id ? t("EDIT_ITEM") : t("ADD_EXPENSE")}
                </DialogTitle>
                <DialogContent>
                    <TextField
                        margin="dense"
                        label={t("NAME")}
                        fullWidth
                        value={currentExpense.name}
                        onChange={(e) =>
                            setCurrentExpense({
                                ...currentExpense,
                                name: e.target.value,
                            })
                        }
                    />
                    <TextField
                        margin="dense"
                        label={t("AMOUNT")}
                        fullWidth
                        value={currentExpense.amount}
                        onChange={(e) =>
                            setCurrentExpense({
                                ...currentExpense,
                                amount: e.target.value,
                            })
                        }
                    />
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                            label={t("DATE")}
                            value={dayjs(currentExpense.addedTime)}
                            onChange={(date) =>
                                setCurrentExpense({
                                    ...currentExpense,
                                    addedTime: date,
                                })
                            }
                        />
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        {t("CANCEL")}
                    </Button>
                    <Button onClick={handleSave} color="primary">
                        {t("SAVE")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Expenses;
